import { useEffect, useLayoutEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

/**
 * ScrollManager — route-change scroll behaviour for BrowserRouter + <Routes>
 * (there is no data router, so <ScrollRestoration> is not available).
 *
 * Where the page scrolls: dashboard routes scroll INSIDE
 * `<main id="gc-main-content">` (the shell is `height: var(--gc-vh)` with
 * `overflow: hidden`, so the window itself does not move there). Standalone
 * routes (/c/:code, /station, /charge/active, /auth, ...) scroll the window.
 * Both are handled.
 *
 * Rules
 *   - PUSH / REPLACE to a different PATHNAME  -> reset window and the shell
 *     scroller to the top, synchronously in a layout effect (before paint), with
 *     `behavior: 'instant'` so there is no visible jump or smooth glide.
 *   - Search-only or hash-only changes, and any navigation that stays on the
 *     same pathname (pagination, filters) -> untouched. Pagination keeps its
 *     own scrollIntoView.
 *   - Navigating to a URL with a #hash -> not reset; if the anchor already
 *     exists it is scrolled into view (React Router does not do this itself).
 *   - POP (back / forward / reload-restored) -> never reset. The shell
 *     scroller's position is remembered per `location.key` and restored
 *     (retrying for ~1s while async content grows tall enough); an entry that
 *     was never scrolled goes to the top, since the scroller outlives the route. The window is
 *     left to the browser's own restoration: `history.scrollRestoration` is
 *     deliberately NOT forced to 'manual', so standalone pages keep native
 *     behaviour and we do not have to re-implement it.
 *   - The charging wizard's own step scroll reset (App.jsx) is untouched.
 *
 * Positions are kept in memory (a small LRU); a full reload of the page loses
 * them, which is the same as the browser dropping an inner scroller's offset.
 */

const SHELL_ID = 'gc-main-content';
const MAX_REMEMBERED = 50;
const RESTORE_ATTEMPTS = 60; // ~1s of animation frames

function getShell() {
  return typeof document === 'undefined' ? null : document.getElementById(SHELL_ID);
}

function scrollElementTo(el, top) {
  if (!el) return;
  if (typeof el.scrollTo === 'function') {
    el.scrollTo({ top, left: 0, behavior: 'instant' });
  } else {
    el.scrollTop = top;
  }
}

function scrollWindowToTop() {
  if (typeof window === 'undefined') return;
  try {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  } catch {
    /* jsdom: not implemented */
  }
}

export default function ScrollManager() {
  const location = useLocation();
  const navType = useNavigationType();

  const prevPathname = useRef(location.pathname);
  const currentKey = useRef(location.key);
  const positions = useRef(new Map());
  const cancelRestore = useRef(null);

  // Remember the shell scroller's offset under the key of the location that is
  // on screen. `scroll` does not bubble, so listen in the capture phase.
  useEffect(() => {
    function onScroll(e) {
      const t = e.target;
      if (!t || t.id !== SHELL_ID) return;
      const map = positions.current;
      map.delete(currentKey.current);
      map.set(currentKey.current, t.scrollTop);
      if (map.size > MAX_REMEMBERED) map.delete(map.keys().next().value);
    }
    document.addEventListener('scroll', onScroll, { capture: true, passive: true });
    return () => document.removeEventListener('scroll', onScroll, { capture: true });
  }, []);

  useLayoutEffect(() => {
    const pathChanged = location.pathname !== prevPathname.current;
    prevPathname.current = location.pathname;
    // Switch the "current" key first so the scroll event the browser fires when
    // the old content is replaced is not filed under the previous entry.
    currentKey.current = location.key;

    if (cancelRestore.current) {
      cancelRestore.current();
      cancelRestore.current = null;
    }

    if (!pathChanged) return undefined;

    if (navType === 'POP') {
      const saved = positions.current.get(location.key);
      if (typeof saved !== 'number' || saved <= 0) {
        // Nothing remembered means that entry was never scrolled. The shell
        // scroller outlives the route, so it still holds the page we just
        // left: put it back to the top rather than inherit that offset.
        scrollElementTo(getShell(), 0);
        return undefined;
      }
      let frames = 0;
      let raf = 0;
      let stopped = false;
      const stop = () => {
        stopped = true;
        if (raf) cancelAnimationFrame(raf);
        ['wheel', 'touchstart', 'keydown'].forEach((evt) => window.removeEventListener(evt, stop, true));
      };
      const attempt = () => {
        if (stopped) return;
        const el = getShell();
        if (el) {
          scrollElementTo(el, saved);
          if (Math.abs(el.scrollTop - saved) <= 1) { stop(); return; }
        }
        if (++frames >= RESTORE_ATTEMPTS) { stop(); return; }
        raf = requestAnimationFrame(attempt);
      };
      // The user taking over the scroll cancels the restore.
      ['wheel', 'touchstart', 'keydown'].forEach((evt) => window.addEventListener(evt, stop, { capture: true, passive: true }));
      attempt();
      cancelRestore.current = stop;
      return undefined;
    }

    // PUSH / REPLACE to a new pathname.
    if (location.hash) {
      let target = null;
      try { target = document.getElementById(decodeURIComponent(location.hash.slice(1))); } catch { /* bad escape */ }
      if (target && typeof target.scrollIntoView === 'function') {
        target.scrollIntoView({ behavior: 'instant', block: 'start' });
      }
      return undefined;
    }

    scrollWindowToTop();
    scrollElementTo(getShell(), 0);
    return undefined;
  }, [location.pathname, location.key, location.hash, navType]);

  useEffect(() => () => { if (cancelRestore.current) cancelRestore.current(); }, []);

  return null;
}
